import {
  CompressOutlined,
  DownloadOutlined,
  EditOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Col, Pagination, Row, Select, Spin, Tag, Tooltip, Upload } from 'antd';
import { debounce } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { setPmContractStatuses } from '../..//contextRedux/PmContractStatus/PmContractStatusStore';
import { PmContract } from '../..//DTOS/PmContract/Response/PmContract';
import CompanyService from '../..//services/company/CompanyService';
import ConfigurationService from '../..//services/configuration/ConfigurationService';
import { useAuth } from '../../context/AuthProvider';
import { setCompanies } from '../../contextRedux/Company/CompanyStore';
import { updateProps } from '../../contextRedux/PMContract/PmContractModel';
import { setPmContracts } from '../../contextRedux/PMContract/PmContractStore';
import { SearchInput } from '../../customComponents/input/SearchInput';
import { CustomTable } from '../../customComponents/table/CustomTable';
import PmContractService from '../../services/pmContract/PmContractService';
import { RootState } from '../../store';
import { Column } from '../../utils/column';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';
import PmContractInformation from './PmContractInformation';

const PmContractPage: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.pmContract.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.pmContract.props.skip);
  const take = useSelector((state: RootState) => state.models?.pmContract.props.take);
  const refresh = useSelector((state: RootState) => state.models?.pmContract.props.refresh);
  const companies = useSelector((state: RootState) => state?.store?.company?.data ?? []);
  const pmcontractStatuses = useSelector((state: RootState) => state?.store?.pmContractStatus?.data ?? []);

  const reduxPmContract = useSelector((state: RootState) => state.models?.pmContract);
  const pmContracts = useSelector((state: RootState) => state.store?.pmContract.data ?? []);

  const [isSearchTermInitialized, setIsSearchTermInitialized] = useState<boolean>(false);

  const [modalInformation, setModalInformation] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<PmContract | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);
  const [visibleMenuRecord, setVisibleMenuRecord] = useState<string | null>(null);
  const menuRef = useRef<any>(null);
  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });
  const currentPage = Math.floor(reduxPmContract.props.skip / reduxPmContract.props.take) + 1;
  const { profile } = useAuth();
  const pageSizes = [10, 20, 30, 50];
  const { message } = App.useApp();

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setVisibleMenuRecord(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    // Convert page/pageSize from URL to skip/take
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take; // fallback to default take if not provided
    // Calculate skip based on pageInUrl and pageSizeInUrl
    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;
    // Dispatch updates to redux store
    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    // Store these initial values in a ref to avoid extra triggers
    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsSearchTermInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isSearchTermInitialized) {
      const queryParams = queryString.parse(location.search);
      // Derive page number from skip/take
      const currentPage = Math.floor(skip / take) + 1;
      // Only update URL if something changed
      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined, // only set if different from default
        };
        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
        // Update the ref to the new state
        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isSearchTermInitialized, navigate, location.pathname, location.search]);

  const handleMenuVisibility = (recordId: string) => {
    setVisibleMenuRecord(visibleMenuRecord === recordId ? null : recordId);
  };

  const handleOptionClick = (action: () => void) => {
    action();
    setVisibleMenuRecord(null);
  };

  const handleModalInformationOpen = (record: any) => {
    setSelectedRecord(record);
    setModalInformation(true);
  };

  const handleModalInformationClose = () => {
    setModalInformation(false);
  };

  const {
    data: _dates,
    refetch,
    isFetching: isPmContractsLoading,
  } = useQuery({
    queryKey: ['pmContracts', searchTerm, skip, take, refresh, selectedCompany, selectedStatus],
    queryFn: () =>
      PmContractService.search({
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        termen: searchTerm || undefined,
        selectedCompany: selectedCompany,
        selectedStatus: selectedStatus,
        pmContractTypeIds: [1, 3],
        skip,
        take,
      }).then((data) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 })); // Update total items
        dispatch(setPmContracts(data?.response));
        return data?.response;
      }),
    enabled: isSearchTermInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch contract statuses for filter
  const { data: _contractStatuses, isFetching: isContractStatusesLoading } = useQuery({
    queryKey: ['pmContractStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmContractStatus({}).then((data) => {
        dispatch(setPmContractStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (isSearchTermInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isSearchTermInitialized, refetch]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      dispatch(updateProps({ field: 'searchTerm', value }));
      dispatch(updateProps({ field: 'skip', value: 0 }));
    }, 500),
    [dispatch]
  );

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  const { mutate: printPdfPmContract, isPending: isPrintingPmContract } = useMutation({
    mutationFn: (record: any) =>
      PmContractService.print({
        ids: [record.id],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const printToPdf = (record: any) => {
    printPdfPmContract(record);
  };

  const { mutate: downloadPdfPmContract, isPending: isDownloadingPmContract } = useMutation({
    mutationFn: (record: any) => PmContractService.downloadFile(record.id),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const downloadPmContract = (record: any) => {
    downloadPdfPmContract(record);
  };

  const { mutate: uploadPdfPmContract, isPending: isUploadingPmContract } = useMutation({
    mutationFn: (formData: any) => PmContractService.upload(formData),
    onSuccess: (response: any) => {
      const data = response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
      } else {
        message.success('PDF incarcat!');
        refetch();
      }
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const handleUpdate = async (id: any, file: any) => {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('file', file);

    await uploadPdfPmContract(formData);
  };

  const handleCompanyChange = (value: string | undefined) => {
    setSelectedCompany(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const handleStatusChange = (value: string | undefined) => {
    setSelectedStatus(value);
    dispatch(updateProps({ field: 'skip', value: 0 }));
  };

  const beforeUpload = (id: any, file: any) => {
    const isPdf = file.type === 'application/pdf';
    if (!isPdf) {
      message.error('Poti adauga doar PDF!');
      return Upload.LIST_IGNORE;
    }
    handleUpdate(id, file);
    return false;
  };

  const getUploadProps = (id: any) => ({
    listType: 'text' as 'text',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file: any) => beforeUpload(id, file),
    onChange: (info: any) => {
      if (info.file.status === 'done') {
        handleUpdate(id, info.file);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} imaginea nu a fost incarcata.`);
      }
      setVisibleMenuRecord(null);
    },
  });

  const paginationConfig = {
    current: currentPage,
    pageSize: take,
    total: reduxPmContract.props.totalItems,
    onChange: (page: number, pageSize: number) => {
      const skipValue = (page - 1) * pageSize;
      dispatch(updateProps({ field: 'skip', value: skipValue }));
      dispatch(updateProps({ field: 'take', value: pageSize }));
    },
    showSizeChanger: true,
    pageSizeOptions: pageSizes.map((size) => size.toString()),
    onShowSizeChange: (_current: number, size: number) => {
      dispatch(updateProps({ field: 'skip', value: 0 }));
      dispatch(updateProps({ field: 'take', value: size }));
    },
    showQuickJumper: false,
  };

  const columns: Column[] = [
    {
      title: 'Contract Id',
      dataIndex: 'id',
      key: 'id',
      hidden: true,
    },
    {
      title: 'Numar',
      dataIndex: 'number',
      key: 'number',
      render: (number: string) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          <Tag color={'blue'}>{number}</Tag>
        </div>
      ),
    },
    {
      title: 'Client',
      key: 'company',
      render: (record: any) => {
        const company = record?.metadata?.company ?? record?.company;
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'blue'}>{company?.name}</Tag>
          </div>
        );
      },
      dataIndex: '',
    },
    {
      title: 'Sisteme',
      dataIndex: '',
      key: 'systemTypes',
      render: (record: any) => {
        const pmContractWorkPlaceItems = record.pmContractWorkPlaceItems ?? [];

        const uniqueSystemTypeNames = Array.from(
          new Set(
            pmContractWorkPlaceItems
              .map((item: any) => item.systemType?.name)
              .filter((name: string | undefined) => !!name)
          )
        );

        const displayCount = 3;
        const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
        const displayNames = shouldShowEllipsis ? uniqueSystemTypeNames.slice(0, displayCount) : uniqueSystemTypeNames;
        const fullList = uniqueSystemTypeNames.join(', ');

        return (
          <Tooltip title={fullList}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                maxWidth: '100%',
              }}
            >
              {displayNames.map((name: any, index: any) => (
                <Tag color='blue' key={index}>
                  {name}
                </Tag>
              ))}
              {shouldShowEllipsis && <Tag color='blue'>...</Tag>}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Data Contract',
      dataIndex: 'date',
      key: 'date',
      render: (contractDate: string) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignContent: 'center',
          }}
        >
          <Tag color={'blue'}>{moment(contractDate).format('DD-MM-YYYY')}</Tag>
        </div>
      ),
    },
    {
      title: 'Data Inceput',
      dataIndex: 'dateStart',
      key: 'dateStart',
      render: (dateStart: string) => moment(dateStart).format('DD-MM-YYYY'),
    },
    {
      title: 'Data Sfarsit',
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      render: (dateEnd: string) => moment(dateEnd).format('DD-MM-YYYY'),
    },
    {
      title: 'Status',
      dataIndex: ['enumPmContractStatus', 'name'],
      key: 'enumPmContractStatus',
      render: (statusnName: string) => (
        <td data-label='Status'>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}
          >
            <Tag color={'purple'}>{statusnName}</Tag>
          </div>
        </td>
      ),
    },
    {
      title: 'Actiune',
      dataIndex: '',
      key: 'actions',
      render: (record: any) => {
        // Create circular menu
        const overlay = (
          <div className='circle-menu'>
            {profile?.userPermission?.rols.includes('admin') && (
              <Tooltip title='Modifica'>
                <Button
                  className='circle-menu-item'
                  icon={<EditOutlined />}
                  onClick={() =>
                    handleOptionClick(() =>
                      window.open(`/project-management/pmContract/edit?id=${record?.id}`, '_blank')
                    )
                  }
                />
              </Tooltip>
            )}
            <Tooltip title='Act Aditional'>
              <Button
                className='circle-menu-item'
                icon={<FileAddOutlined />}
                onClick={() =>
                  handleOptionClick(() =>
                    window.open(`/project-management/pmContract/pmact-additionally/edit?id=${record?.id}`, '_blank')
                  )
                }
              />
            </Tooltip>
            <Tooltip title='Incarca Imagine Contract'>
              <div className='circle-menu-item'>
                <Upload {...getUploadProps(record.id)}>
                  <Button
                    icon={<UploadOutlined />}
                    style={{ borderRadius: '50%', width: 40, height: 40, backgroundColor: '#e1e1e1' }}
                  />
                </Upload>
              </div>
            </Tooltip>
            <Tooltip title='Informatii'>
              <Button
                className='circle-menu-item'
                icon={<InfoCircleOutlined />}
                onClick={() => handleOptionClick(() => handleModalInformationOpen(record))}
              />
            </Tooltip>
            {!record?.haveFile ? (
              <Tooltip title='Descarca PDF'>
                <Button
                  className='circle-menu-item'
                  icon={<DownloadOutlined />}
                  onClick={() => handleOptionClick(() => printToPdf(record))}
                />
              </Tooltip>
            ) : (
              <Tooltip title='Descarca Contract Semnat'>
                <Button
                  className='circle-menu-item'
                  icon={<DownloadOutlined />}
                  onClick={() => handleOptionClick(() => downloadPmContract(record))}
                />
              </Tooltip>
            )}
          </div>
        );

        return (
          <div>
            {visibleMenuRecord === record.id && overlay}
            <Button onClick={() => handleMenuVisibility(record.id)} className='plus_edit'>
              {visibleMenuRecord === record.id ? <CompressOutlined /> : <PlusOutlined />}
            </Button>
          </div>
        );
      },
      width: 'auto',
      fixed: 'right',
    },
  ];

  if (
    isPmContractsLoading ||
    isPmContractsLoading ||
    isPrintingPmContract ||
    isDownloadingPmContract ||
    isUploadingPmContract ||
    isCompaniesLoading ||
    isContractStatusesLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <Row justify='space-between' align='middle' style={{ marginBottom: '20px' }}>
        <Col>
          <h1>Contracte Instalari</h1>
        </Col>

        <Col>
          <Tooltip title='Creeaza contract' trigger={'hover'} placement='left'>
            <Button
              type='primary'
              onClick={() => window.open(`/project-management/pmContract/new`, '_blank')}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={8}>
          <SearchInput
            placeholder='Search...'
            defaultValue={searchTerm}
            onChange={(e: any) => handleSearch(e.target.value)}
            autoFocus={true}
            style={{ width: '100%' }} // Ensures input takes full width
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa beneficiar'
            value={selectedCompany}
            onChange={handleCompanyChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {companies?.map((company: any) => (
              <Select.Option key={company?.id} value={company?.name}>
                {company?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            placeholder='Filtreaza dupa status'
            value={selectedStatus}
            onChange={handleStatusChange}
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp='children'
          >
            {pmcontractStatuses?.map((status: any) => (
              <Select.Option key={status?.id} value={status?.name}>
                {status?.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <PmContractInformation
        handleModalInformationClose={handleModalInformationClose}
        modalInformation={modalInformation}
        selectedRecord={selectedRecord}
      />
      {isMobile ? (
        <div className='table-mobile-view'>
          {pmContracts.map((pmContract) => (
            <div className='mobile-view-background' key={pmContract.id}>
              <div className='mobile-view-header'>
                <Tag color='purple'>{pmContract.number}</Tag>
                <Button
                  onClick={() => handleMenuVisibility(pmContract.id)}
                  shape='circle'
                  icon={visibleMenuRecord === pmContract.id ? <CompressOutlined /> : <PlusOutlined />}
                />
              </div>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Client:</strong>{' '}
                <Tooltip title={pmContract.company?.name}>
                  <Tag className='client-tag' color='blue'>
                    {' '}
                    {pmContract.company?.name}
                  </Tag>
                </Tooltip>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Contract:</strong>{' '}
                <Tag color={'blue'}>{moment(pmContract.date).format('DD-MM-YYYY')}</Tag>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Inceput:</strong>{' '}
                {moment(pmContract.dateStart).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Data Sfarsit:</strong>{' '}
                {moment(pmContract.dateEnd).format('DD-MM-YYYY')}
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Sisteme:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'end' }}>
                  {(() => {
                    const uniqueSystemTypeNames = Array.from(
                      new Set(
                        (pmContract.pmContractWorkPlaceItems ?? [])
                          .map((item: any) => item.systemType?.name)
                          .filter((name: string | undefined) => !!name)
                      )
                    );

                    const displayCount = 3;
                    const shouldShowEllipsis = uniqueSystemTypeNames.length > displayCount;
                    const displayNames = shouldShowEllipsis
                      ? uniqueSystemTypeNames.slice(0, displayCount)
                      : uniqueSystemTypeNames;
                    const fullList = uniqueSystemTypeNames.join(', ');

                    return (
                      <Tooltip title={fullList}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {displayNames.map((name: any, index: any) => (
                            <Tag
                              key={index}
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              {name}
                            </Tag>
                          ))}
                          {shouldShowEllipsis && (
                            <Tag
                              color='blue'
                              style={{
                                maxWidth: '120px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer',
                              }}
                            >
                              ...
                            </Tag>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })()}
                </div>
              </Col>
              <Col span={24} className='CardMobile-item'>
                <strong className='CardMobile-item-label'>Status:</strong>{' '}
                <Tag color='green'>{pmContract.enumPmContractStatus?.name}</Tag>
              </Col>

              {/* Action Buttons */}
              {visibleMenuRecord === pmContract.id && (
                <div className='visible-menu-action-btn'>
                  <Tooltip title='Modifica'>
                    <Button
                      className='modify'
                      shape='circle'
                      icon={<EditOutlined />}
                      onClick={() => window.open(`/project-management/pmContract/edit?id=${pmContract.id}`, '_blank')}
                    />
                  </Tooltip>
                  <Tooltip title='Act Aditional'>
                    <Button
                      className='additional'
                      shape='circle'
                      icon={<FileAddOutlined />}
                      onClick={() =>
                        window.open(
                          `/project-management/pmContract/pmact-additionally/edit?id=${pmContract?.id}`,
                          '_blank'
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip title='Incarca Imagine Contract'>
                    <Upload {...getUploadProps(pmContract.id)}>
                      <Button className='upload' shape='circle' icon={<UploadOutlined />} />
                    </Upload>
                  </Tooltip>
                  <Tooltip title='Informatii'>
                    <Button
                      className='information'
                      shape='circle'
                      icon={<InfoCircleOutlined />}
                      onClick={() => handleModalInformationOpen(pmContract)}
                    />
                  </Tooltip>
                  <Tooltip title='Descarca PDF'>
                    <Button
                      className='pdfDownload'
                      shape='circle'
                      icon={<DownloadOutlined />}
                      onClick={() => printToPdf(pmContract)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}

          {/* Pagination for Mobile */}
          <Pagination
            className='mobile-view-pagination'
            current={currentPage}
            pageSize={take}
            total={reduxPmContract.props.totalItems}
            onChange={(page, pageSize) => {
              dispatch(updateProps({ field: 'skip', value: (page - 1) * pageSize }));
              dispatch(updateProps({ field: 'take', value: pageSize }));
            }}
            showSizeChanger={true}
            pageSizeOptions={pageSizes.map((size) => size.toString())}
            onShowSizeChange={(_current, size) => {
              dispatch(updateProps({ field: 'skip', value: 0 }));
              dispatch(updateProps({ field: 'take', value: size }));
            }}
            showLessItems
          />
        </div>
      ) : (
        // Desktop View (Table)
        <CustomTable
          initialDataSource={pmContracts}
          pagination={paginationConfig}
          columns={columns}
          rowKey='id'
          scroll={{ x: true }}
          className='large-view-table'
        />
      )}
    </>
  );
};

export default PmContractPage;
